import { daysAgo } from '@doss/common-all'
import { Box, Theme, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { groupBy, orderBy } from 'lodash'
import { Lock, SlidersHorizontal } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { RxCross2 } from 'react-icons/rx'
import InfiniteScroll from 'react-infinite-scroller'
import { cn } from '~/@/lib/utils'
import { useActivityFeed } from '~/common/hooks/useActivityFeed'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { usePage } from '~/common/hooks/usePage'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import DossSwitch from '~/components/shared/DossSwitch'
import Placeholder, { PlaceholderType } from '~/components/shared/Placeholder'
import { Dropdown } from '~/components/ui/molecules/dropdown/Dropdown'
import { ENVIRONMENT_BANNER_HEIGHT } from '../EnvironmentBanner'
import { ActivityTimeline } from './ActivityTimeline/ActivityTimeline'
import { CommentField } from './comment/CommentField'
import { PaginatedComment, PaginatedEvent } from './types'

const titleHeight = 56 + 'px'
const headerHeight = 56 + ENVIRONMENT_BANNER_HEIGHT + 'px'
const activityWidth = 420 + 'px'

function getStyles(theme: Theme) {
  return {
    container: {
      width: activityWidth,
      height: `calc(100% - ${headerHeight})`,
      overflow: 'hidden',
      marginTop: headerHeight,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: theme.spacing(1.5),
      paddingInline: theme.spacing(2),
      paddingBottom: theme.spacing(1.5),
      height: titleHeight,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    addComment: {
      paddingBlock: theme.spacing(2),
      paddingInline: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    commentInput: {
      '& .MuiInputBase-root': {
        alignItems: 'center',
      },
    },
    dayTitle: {
      marginInlineStart: theme.spacing(2),
    },
    activity: {
      paddingTop: theme.spacing(2),
      overflowY: 'auto',
      overflowX: 'hidden',
      maxWidth: '100%',
      height: `calc(100% - ${headerHeight})`,
    },
    footer: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(4),
      height: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    ender: {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(2),
    },
  }
}

interface TimelineDrawerProps {
  onClose: () => void
}

export const ACTIVITY_LIMIT = 10

type TimelineActivity = (PaginatedComment | PaginatedEvent) & {
  createdAt: string
}

export function TimelineDrawer({ onClose }: TimelineDrawerProps) {
  const { t } = useTypedTranslation()
  const styles = useStyles(getStyles)
  const { objectId, objectType } = usePage()
  const { isLoading: isUserLoading } = useGlobalContext()
  const {
    activeTab,
    canToggleRowEvents,
    canViewActivity,
    comments,
    events,
    fetchMore,
    hasMore,
    includeRowEvents,
    loading,
    scopeOptions,
    selectedScope,
    setActiveTab,
    setIncludeRowEvents,
    setSelectedScopeId,
  } = useActivityFeed()

  const timelineEvents = useMemo<TimelineActivity[]>(
    () =>
      events.map((event) => ({
        ...event,
        createdAt: event.event.timestamp,
      })),
    [events],
  )
  const timelineComments = useMemo<TimelineActivity[]>(
    () =>
      comments.map((comment) => ({
        ...comment,
        createdAt: comment.comment.createdAt,
      })),
    [comments],
  )
  const activities = useMemo<TimelineActivity[]>(() => {
    const timelineActivities = activeTab === 'activity' ? timelineEvents : timelineComments
    return orderBy(timelineActivities, (activity) => activity.createdAt, 'desc')
  }, [activeTab, timelineComments, timelineEvents])

  const byDay: Record<string, TimelineActivity[]> = useMemo(() => {
    return groupBy(activities, (activity) => {
      const date = dayjs(activity.createdAt)
      return date.format('MM-DD-YYYY')
    })
  }, [activities])

  const handleLoadMore = useCallback(async () => {
    if (!hasMore) {
      return
    }

    await fetchMore()
  }, [fetchMore, hasMore])

  const tabOptions = useMemo(
    () => [
      { label: t('activity.tabs.activity'), value: 'activity' as const },
      { label: t('activity.tabs.comments'), value: 'comments' as const },
    ],
    [t],
  )

  return (
    <div className="flex size-full flex-col overflow-hidden">
      <div className="box-content flex h-[60px] items-center justify-between border-b px-4">
        <div className="text-sm">{t('activity.title')}</div>
        <div
          className="flex size-6 cursor-pointer items-center justify-center hover:bg-gray-1"
          onClick={onClose}
        >
          <RxCross2 className="text-base" />
        </div>
      </div>
      <div className="flex min-h-14 items-center justify-center border-b p-2 px-5">
        <CommentField objectId={objectId} objectType={objectType} />
      </div>
      <div className="flex h-12 items-end gap-8 border-b px-4">
        {tabOptions.map((tab) => (
          <button
            key={tab.value}
            className={cn(
              'h-full border-b-2 text-sm',
              activeTab === tab.value
                ? 'border-gray-12 text-gray-12'
                : 'border-transparent text-gray-8',
            )}
            onClick={() => setActiveTab(tab.value)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'activity' && canViewActivity && canToggleRowEvents && (
        <label className="flex min-h-12 cursor-pointer items-center justify-between border-b px-4 text-sm text-gray-11">
          {t('activity.show_row_activity')}
          <Box
            sx={{
              '&& .MuiSwitch-track': {
                backgroundColor: (theme) => theme.palette.common.doss.gray[6],
              },
            }}
          >
            <DossSwitch
              checked={includeRowEvents}
              onChange={(_, checked) => setIncludeRowEvents(checked)}
              size="sm"
            />
          </Box>
        </label>
      )}
      {activeTab === 'activity' && canViewActivity && scopeOptions.length > 1 && selectedScope && (
        <div className="flex min-h-12 items-center border-b px-4">
          <Dropdown
            className="w-64"
            disableClearable
            getOptionLabel={(option) => option.label}
            onChange={(_, option) => setSelectedScopeId(option.id)}
            options={scopeOptions}
            size="small"
            startAdornment={<SlidersHorizontal className="size-3" />}
            value={selectedScope}
          />
        </div>
      )}
      {activeTab === 'activity' && !isUserLoading && !canViewActivity ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-gray-9">
          <Lock className="size-5" />
          <span className="text-sm">{t('activity.admin_only')}</span>
        </div>
      ) : (
        <>
          {loading && <Placeholder type={PlaceholderType.ACTIVITY} />}
          <div className="flex h-full flex-1 flex-col overflow-y-auto py-4">
            <InfiniteScroll hasMore={hasMore} loadMore={handleLoadMore} useWindow={false}>
              <Box>
                {Object.keys(byDay).map((day) => (
                  <Box key={day}>
                    <Typography sx={styles.dayTitle} variant="subtitle1">
                      {daysAgo(day)}
                    </Typography>
                    <ActivityTimeline activities={byDay[day]} />
                  </Box>
                ))}
              </Box>
            </InfiniteScroll>
            {!hasMore && !loading && Object.keys(byDay).length > 0 && (
              <Box sx={styles.footer}>
                <Typography align="center" sx={styles.ender} variant="body2">
                  {t('activity.no_more_activity')}
                </Typography>
              </Box>
            )}
          </div>
        </>
      )}
    </div>
  )
}
